/**
 * Role Definitions for Estate Planning Documents
 *
 * This configuration maps document types to available roles and defines
 * which placeholder fields each role populates.
 */

// All available roles across all document types
export const ROLE_DEFINITIONS = {
  // Client/Principal role (special - typically the person creating the documents)
  client: {
    id: 'client',
    label: 'Client',
    description: 'The person creating the estate plan',
    category: 'primary',
    placeholderFields: ['client'],
    requiresRelationship: false,
    requiresAddress: false,
    requiresPhone: false,
  },

  // Spouse role
  spouse: {
    id: 'spouse',
    label: 'Spouse',
    description: 'Spouse of the client',
    category: 'family',
    placeholderFields: ['spouse'],
    requiresRelationship: false, // Relationship is implicitly "spouse"
    requiresAddress: false,
    requiresPhone: false,
  },

  // Will-specific roles
  executor: {
    id: 'executor',
    label: 'Executor',
    description: 'Person responsible for administering the will',
    category: 'fiduciary',
    placeholderFields: ['executor_name', 'executor_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  successor_executor: {
    id: 'successor_executor',
    label: 'Successor Executor',
    description: 'Backup person to administer the will',
    category: 'fiduciary',
    placeholderFields: ['successor_executor_name', 'successor_executor_relationship'],
    aliasFields: ['first_successor_executor_name'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  guardian: {
    id: 'guardian',
    label: 'Guardian',
    description: 'Person to care for minor children',
    category: 'fiduciary',
    placeholderFields: ['guardian_name', 'guardian_relationship'],
    aliasFields: ['guardian'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  successor_guardian: {
    id: 'successor_guardian',
    label: 'Successor Guardian',
    description: 'Backup person to care for minor children',
    category: 'fiduciary',
    placeholderFields: ['successor_guardian_name', 'successor_guardian_relationship'],
    aliasFields: ['successor_guardian', 'first_successor_guardian_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },

  // Trust-specific roles
  successor_trustee: {
    id: 'successor_trustee',
    label: 'Successor Trustee',
    description: 'First person to manage the trust if grantor cannot',
    category: 'fiduciary',
    placeholderFields: ['successor_trustee_name', 'successor_trustee_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  second_successor_trustee: {
    id: 'second_successor_trustee',
    label: 'Second Successor Trustee',
    description: 'Second backup trustee',
    category: 'fiduciary',
    placeholderFields: ['second_successor_trustee_name', 'second_successor_trustee_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  third_successor_trustee: {
    id: 'third_successor_trustee',
    label: 'Third Successor Trustee',
    description: 'Third backup trustee',
    category: 'fiduciary',
    placeholderFields: ['third_successor_trustee_name', 'third_successor_trustee_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  first_successor_trust_protector: {
    id: 'first_successor_trust_protector',
    label: 'First Successor Trust Protector',
    description: 'Person who can modify trust terms if needed',
    category: 'fiduciary',
    placeholderFields: ['first_successor_trust_protector_name', 'first_successor_trust_protector_relationship'],
    aliasFields: ['successor_trust_protector_name', 'successor_trust_protector_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  second_successor_trust_protector: {
    id: 'second_successor_trust_protector',
    label: 'Second Successor Trust Protector',
    description: 'Backup trust protector',
    category: 'fiduciary',
    placeholderFields: ['second_successor_trust_protector_name', 'second_successor_trust_protector_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  third_successor_trust_protector: {
    id: 'third_successor_trust_protector',
    label: 'Third Successor Trust Protector',
    description: 'Third backup trust protector',
    category: 'fiduciary',
    placeholderFields: ['third_successor_trust_protector_name', 'third_successor_trust_protector_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },

  // 3rd-level roles (Probate Avoidance)
  second_successor_executor: {
    id: 'second_successor_executor',
    label: 'Second Successor Executor',
    description: 'Third backup person to administer the will',
    category: 'fiduciary',
    placeholderFields: ['second_successor_executor_name', 'second_successor_executor_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  second_successor_guardian: {
    id: 'second_successor_guardian',
    label: 'Second Successor Guardian',
    description: 'Third backup person to care for minor children',
    category: 'fiduciary',
    placeholderFields: ['second_successor_guardian', 'second_successor_guardian_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },

  // Estate Protector role (Probate Avoidance)
  estate_protector: {
    id: 'estate_protector',
    label: 'Estate Protector',
    description: 'Person who oversees the estate plan',
    category: 'fiduciary',
    placeholderFields: ['estate_protector'],
    requiresRelationship: false,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: false,
    maxCoFiduciaries: 1,
  },

  // Financial POA roles
  financial_agent: {
    id: 'financial_agent',
    label: 'Financial Agent',
    description: 'Person authorized to handle financial matters',
    category: 'fiduciary',
    placeholderFields: ['agent_name', 'agent_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  successor_financial_agent: {
    id: 'successor_financial_agent',
    label: 'Successor Financial Agent',
    description: 'Backup person for financial matters',
    category: 'fiduciary',
    placeholderFields: ['successor_agent_name', 'successor_agent_relationship'],
    requiresRelationship: true,
    requiresAddress: false,
    requiresPhone: false,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },

  // Healthcare POA roles (require full contact info)
  healthcare_agent: {
    id: 'healthcare_agent',
    label: 'Healthcare Agent',
    description: 'Person authorized to make medical decisions',
    category: 'fiduciary',
    placeholderFields: [
      'healthcare_agent_name',
      'healthcare_agent_street_address',
      'healthcare_agent_city_and_state_and_zipcode',
      'healthcare_agent_telephone'
    ],
    requiresRelationship: false,
    requiresAddress: true,
    requiresPhone: true,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  first_successor_healthcare_agent: {
    id: 'first_successor_healthcare_agent',
    label: 'First Successor Healthcare Agent',
    description: 'First backup for medical decisions',
    category: 'fiduciary',
    placeholderFields: [
      'first_successor_healthcare_agent_name',
      'first_successor_healthcare_agent_street_address',
      'first_successor_healthcare_agent_city_and_state_and_zipcode',
      'first_successor_healthcare_agent_telephone'
    ],
    requiresRelationship: false,
    requiresAddress: true,
    requiresPhone: true,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
  second_successor_healthcare_agent: {
    id: 'second_successor_healthcare_agent',
    label: 'Second Successor Healthcare Agent',
    description: 'Second backup for medical decisions',
    category: 'fiduciary',
    placeholderFields: [
      'second_successor_healthcare_agent_name',
      'second_successor_healthcare_agent_street_address',
      'second_successor_healthcare_agent_city_and_state_and_zipcode',
      'second_successor_healthcare_agent_telephone'
    ],
    requiresRelationship: false,
    requiresAddress: true,
    requiresPhone: true,
    allowsCoFiduciary: true,
    maxCoFiduciaries: 3,
  },
};

// Map document types to their available roles
export const DOCUMENT_TYPE_ROLES = {
  // Will documents
  'Will': [
    'executor',
    'successor_executor',
    'guardian',
    'successor_guardian',
  ],
  'Pour-Over Will': [
    'spouse',
    'executor',
    'successor_executor',
    'guardian',
    'successor_guardian',
  ],

  // Trust documents
  'Trust': [
    'spouse',
    'successor_trustee',
    'second_successor_trustee',
    'third_successor_trustee',
    'first_successor_trust_protector',
    'second_successor_trust_protector',
    'third_successor_trust_protector',
  ],
  'Revocable Living Trust': [
    'spouse',
    'successor_trustee',
    'second_successor_trustee',
    'third_successor_trustee',
    'first_successor_trust_protector',
    'second_successor_trust_protector',
    'third_successor_trust_protector',
  ],
  'Fractional QTIP Trust': [
    'spouse',
    'successor_trustee',
    'second_successor_trustee',
    'third_successor_trustee',
    'first_successor_trust_protector',
    'second_successor_trust_protector',
    'third_successor_trust_protector',
  ],

  // Power of Attorney documents
  'Financial Power of Attorney': [
    'financial_agent',
    'successor_financial_agent',
  ],
  'Healthcare Power of Attorney': [
    'healthcare_agent',
    'first_successor_healthcare_agent',
    'second_successor_healthcare_agent',
  ],

  // HIPAA - no special roles beyond client
  'HIPAA Authorization': [],

  // Healthcare package - only healthcare agent roles
  'Healthcare Documents Package': [
    'healthcare_agent',
    'first_successor_healthcare_agent',
    'second_successor_healthcare_agent',
  ],

  // Packages (combination of multiple document types)
  'Disclaimer Package': [
    'spouse',
    'executor',
    'successor_executor',
    'guardian',
    'successor_guardian',
    'successor_trustee',
    'second_successor_trustee',
    'third_successor_trustee',
    'first_successor_trust_protector',
    'second_successor_trust_protector',
    'third_successor_trust_protector',
    'financial_agent',
    'successor_financial_agent',
    'healthcare_agent',
    'first_successor_healthcare_agent',
    'second_successor_healthcare_agent',
  ],
  'Fractional QTIP Package': [
    'spouse',
    'executor',
    'successor_executor',
    'guardian',
    'successor_guardian',
    'successor_trustee',
    'second_successor_trustee',
    'third_successor_trustee',
    'first_successor_trust_protector',
    'second_successor_trust_protector',
    'third_successor_trust_protector',
    'financial_agent',
    'successor_financial_agent',
    'healthcare_agent',
    'first_successor_healthcare_agent',
    'second_successor_healthcare_agent',
  ],
  'Probate Avoidance Package': [
    'spouse',
    'executor',
    'successor_executor',
    'second_successor_executor',
    'guardian',
    'successor_guardian',
    'second_successor_guardian',
    'estate_protector',
    'successor_trustee',
    'second_successor_trustee',
    'first_successor_trust_protector',
    'second_successor_trust_protector',
    'financial_agent',
    'successor_financial_agent',
    'healthcare_agent',
    'first_successor_healthcare_agent',
    'second_successor_healthcare_agent',
  ],
};

// Role categories for grouping in UI (legacy - kept for backward compatibility)
export const ROLE_CATEGORIES = {
  primary: {
    label: 'Primary',
    description: 'The person creating the documents',
    order: 1,
  },
  family: {
    label: 'Family',
    description: 'Family members',
    order: 2,
  },
  fiduciary: {
    label: 'Fiduciary Roles',
    description: 'People who will manage your affairs',
    order: 3,
  },
};

// Role Types - groups roles by function with position slots
// This provides a more intuitive UI where users select "Executor: 1st, 2nd" etc.
export const ROLE_TYPES = {
  spouse: {
    id: 'spouse',
    label: 'Spouse',
    description: 'Spouse of the client',
    order: 0.5,
    positions: [
      { position: 1, label: '1st', roleId: 'spouse' },
    ]
  },
  executor: {
    id: 'executor',
    label: 'Executor',
    description: 'Person responsible for administering the will',
    order: 1,
    positions: [
      { position: 1, label: '1st', roleId: 'executor' },
      { position: 2, label: '2nd', roleId: 'successor_executor' },
      { position: 3, label: '3rd', roleId: 'second_successor_executor' },
    ]
  },
  trustee: {
    id: 'trustee',
    label: 'Successor Trustee',
    description: 'Successor trustees to manage trust assets (client is primary trustee)',
    order: 2,
    positions: [
      { position: 1, label: '1st', roleId: 'successor_trustee' },
      { position: 2, label: '2nd', roleId: 'second_successor_trustee' },
      { position: 3, label: '3rd', roleId: 'third_successor_trustee' },
    ]
  },
  guardian: {
    id: 'guardian',
    label: 'Guardian',
    description: 'Person to care for minor children',
    order: 3,
    positions: [
      { position: 1, label: '1st', roleId: 'guardian' },
      { position: 2, label: '2nd', roleId: 'successor_guardian' },
      { position: 3, label: '3rd', roleId: 'second_successor_guardian' },
    ]
  },
  estate_protector: {
    id: 'estate_protector',
    label: 'Estate Protector',
    description: 'Person who oversees the estate plan',
    order: 3.5,
    positions: [
      { position: 1, label: '1st', roleId: 'estate_protector' },
    ]
  },
  trust_protector: {
    id: 'trust_protector',
    label: 'Successor Trust Protector',
    description: 'Successor trust protectors who can modify trust terms (client is primary)',
    order: 4,
    positions: [
      { position: 1, label: '1st', roleId: 'first_successor_trust_protector' },
      { position: 2, label: '2nd', roleId: 'second_successor_trust_protector' },
      { position: 3, label: '3rd', roleId: 'third_successor_trust_protector' },
    ]
  },
  financial_agent: {
    id: 'financial_agent',
    label: 'Financial Agent',
    description: 'Person to handle financial matters (Power of Attorney)',
    order: 5,
    positions: [
      { position: 1, label: '1st', roleId: 'financial_agent' },
      { position: 2, label: '2nd', roleId: 'successor_financial_agent' },
    ]
  },
  healthcare_agent: {
    id: 'healthcare_agent',
    label: 'Healthcare Agent',
    description: 'Person to make medical decisions',
    order: 6,
    positions: [
      { position: 1, label: '1st', roleId: 'healthcare_agent' },
      { position: 2, label: '2nd', roleId: 'first_successor_healthcare_agent' },
      { position: 3, label: '3rd', roleId: 'second_successor_healthcare_agent' },
    ]
  },
};

// Common relationship options
export const RELATIONSHIP_OPTIONS = [
  'spouse',
  'wife',
  'husband',
  'son',
  'daughter',
  'son-in-law',
  'daughter-in-law',
  'brother',
  'sister',
  'brother-in-law',
  'sister-in-law',
  'father',
  'mother',
  'father-in-law',
  'mother-in-law',
  'uncle',
  'aunt',
  'nephew',
  'niece',
  'cousin',
  'grandson',
  'granddaughter',
  'stepson',
  'stepdaughter',
  'stepfather',
  'stepmother',
  'friend',
  'attorney',
  'accountant',
  'financial advisor',
];

/**
 * Get available roles for a document type
 * @param {string} documentType - The document type or package name
 * @returns {Array} Array of role IDs available for this document type
 */
export function getRolesForDocumentType(documentType) {
  // Direct match
  if (DOCUMENT_TYPE_ROLES[documentType]) {
    return DOCUMENT_TYPE_ROLES[documentType];
  }

  // Try case-insensitive match
  const normalizedType = documentType.toLowerCase();
  for (const [key, roles] of Object.entries(DOCUMENT_TYPE_ROLES)) {
    if (key.toLowerCase() === normalizedType) {
      return roles;
    }
  }

  // Check for partial matches (e.g., "QTIP" in "Fractional QTIP Package")
  for (const [key, roles] of Object.entries(DOCUMENT_TYPE_ROLES)) {
    if (normalizedType.includes(key.toLowerCase()) ||
        key.toLowerCase().includes(normalizedType)) {
      return roles;
    }
  }

  // Default: return all fiduciary roles for unknown document types
  return Object.keys(ROLE_DEFINITIONS).filter(
    roleId => ROLE_DEFINITIONS[roleId].category === 'fiduciary'
  );
}

/**
 * Get role definition by ID
 * @param {string} roleId - The role ID
 * @returns {Object} Role definition object
 */
export function getRoleDefinition(roleId) {
  return ROLE_DEFINITIONS[roleId] || null;
}

/**
 * Get role types available for a document type
 * Returns ROLE_TYPES filtered to only include types where at least one position is needed
 * @param {string} documentType - The document type or package name
 * @returns {Array} Array of role type objects with filtered positions
 */
export function getRoleTypesForDocumentType(documentType) {
  const availableRoleIds = getRolesForDocumentType(documentType);
  const availableRoleIdSet = new Set(availableRoleIds);

  // Filter ROLE_TYPES to only include types with at least one available position
  const filteredTypes = [];

  for (const [typeId, roleType] of Object.entries(ROLE_TYPES)) {
    // Filter positions to only those available for this document type
    const availablePositions = roleType.positions.filter(pos =>
      availableRoleIdSet.has(pos.roleId)
    );

    // Only include this role type if it has available positions
    if (availablePositions.length > 0) {
      filteredTypes.push({
        ...roleType,
        positions: availablePositions,
      });
    }
  }

  // Sort by order
  return filteredTypes.sort((a, b) => a.order - b.order);
}

/**
 * Get available roles for multiple document types (aggregated)
 * @param {Array<string>} documentTypes - Array of document type or package names
 * @returns {Array} Array of unique role IDs available across all document types
 */
export function getRolesForDocumentTypes(documentTypes) {
  if (!Array.isArray(documentTypes) || documentTypes.length === 0) {
    return [];
  }

  // Aggregate roles from all document types
  const allRoles = new Set();
  for (const docType of documentTypes) {
    const roles = getRolesForDocumentType(docType);
    roles.forEach(role => allRoles.add(role));
  }

  return Array.from(allRoles);
}

/**
 * Get role types available for multiple document types (aggregated)
 * @param {Array<string>} documentTypes - Array of document type or package names
 * @returns {Array} Array of role type objects with positions from all document types
 */
export function getRoleTypesForDocumentTypes(documentTypes) {
  if (!Array.isArray(documentTypes) || documentTypes.length === 0) {
    return [];
  }

  // Get aggregated role IDs
  const availableRoleIds = getRolesForDocumentTypes(documentTypes);
  const availableRoleIdSet = new Set(availableRoleIds);

  // Filter ROLE_TYPES to only include types with at least one available position
  const filteredTypes = [];

  for (const [typeId, roleType] of Object.entries(ROLE_TYPES)) {
    // Filter positions to only those available for these document types
    const availablePositions = roleType.positions.filter(pos =>
      availableRoleIdSet.has(pos.roleId)
    );

    // Only include this role type if it has available positions
    if (availablePositions.length > 0) {
      filteredTypes.push({
        ...roleType,
        positions: availablePositions,
      });
    }
  }

  // Sort by order
  return filteredTypes.sort((a, b) => a.order - b.order);
}

// Fields that represent names but don't contain '_name' in their key
const NAME_ONLY_FIELDS = new Set([
  'client', 'spouse', 'estate_protector', 'guardian',
  'successor_guardian', 'second_successor_guardian'
]);

/**
 * Generate placeholder values from a person's data and assigned role
 * @param {Object} person - Person object with name, relationship, address, etc.
 * @param {string} roleId - The role ID to generate placeholders for
 * @returns {Object} Object with placeholder names as keys and values
 */
export function generatePlaceholdersForRole(person, roleId) {
  const role = ROLE_DEFINITIONS[roleId];
  if (!role) return {};

  const placeholders = {};

  const allFields = [...role.placeholderFields];
  if (role.aliasFields) allFields.push(...role.aliasFields);

  for (const field of allFields) {
    if (field.includes('_name') || NAME_ONLY_FIELDS.has(field)) {
      placeholders[field] = person.name || '';
    } else if (field.includes('_relationship')) {
      placeholders[field] = person.relationship || '';
    } else if (field.includes('_street_address')) {
      placeholders[field] = person.streetAddress || '';
    } else if (field.includes('_city_and_state_and_zipcode')) {
      placeholders[field] = person.cityStateZip || '';
    } else if (field.includes('_telephone') || field.includes('_phone')) {
      placeholders[field] = person.phone || '';
    }
  }

  return placeholders;
}

/**
 * Generate placeholder values for a role with multiple co-fiduciaries
 * @param {Array<Object>} people - Array of person objects assigned to this role
 * @param {string} roleId - The role ID
 * @returns {Object} Object with placeholder names as keys and aggregated values
 */
export function generatePlaceholdersForCoFiduciaries(people, roleId) {
  const role = ROLE_DEFINITIONS[roleId];
  if (!role || people.length === 0) return {};

  // If only one person, use standard logic
  if (people.length === 1) {
    return generatePlaceholdersForRole(people[0], roleId);
  }

  const placeholders = {};

  const allFields = [...role.placeholderFields];
  if (role.aliasFields) allFields.push(...role.aliasFields);

  // Find the name and relationship fields for this role (check all fields including aliases)
  const nameField = allFields.find(f =>
    f.includes('_name') || NAME_ONLY_FIELDS.has(f)
  );
  const relationshipField = allFields.find(f =>
    f.includes('_relationship')
  );

  // Find ALL name and relationship fields (primary + aliases)
  const allNameFields = allFields.filter(f =>
    f.includes('_name') || NAME_ONLY_FIELDS.has(f)
  );
  const allRelationshipFields = allFields.filter(f =>
    f.includes('_relationship')
  );

  // For co-fiduciaries, populate BOTH name and relationship fields separately
  if (nameField && relationshipField) {
    // Collect names separately (comma-separated, backend format_list() will convert to "and")
    const names = people.map(p => p.name).filter(n => n && n.trim());
    const joinedNames = names.join(', ');
    for (const nf of allNameFields) {
      placeholders[nf] = joinedNames;
    }

    // Collect relationships separately (with "my" prefix for proper grammar)
    const relationships = people.map((p, index) => {
      if (p.relationship && p.relationship.trim()) {
        // First person uses template's "my", subsequent use their own "my"
        const prefix = index === 0 ? 'my ' : 'my ';
        return `${prefix}${p.relationship}`;
      }
      return '';
    }).filter(r => r);
    const joinedRelationships = relationships.join(', ');
    for (const rf of allRelationshipFields) {
      placeholders[rf] = joinedRelationships;
    }
  } else if (nameField) {
    // If only name field exists, just populate names
    const names = people.map(p => p.name).filter(n => n && n.trim());
    const joinedNames = names.join(', ');
    for (const nf of allNameFields) {
      placeholders[nf] = joinedNames;
    }
  }

  // Handle other fields (addresses, phones)
  for (const field of allFields) {
    if (field.includes('_street_address')) {
      // Use first person's address for co-fiduciaries
      placeholders[field] = people[0]?.streetAddress || '';

    } else if (field.includes('_city_and_state_and_zipcode')) {
      placeholders[field] = people[0]?.cityStateZip || '';

    } else if (field.includes('_telephone') || field.includes('_phone')) {
      placeholders[field] = people[0]?.phone || '';
    }
  }

  return placeholders;
}

/**
 * Check if a role requires certain fields to be filled
 * @param {string} roleId - The role ID
 * @returns {Object} Object with required field flags
 */
export function getRoleRequirements(roleId) {
  const role = ROLE_DEFINITIONS[roleId];
  if (!role) {
    return { requiresRelationship: false, requiresAddress: false, requiresPhone: false };
  }
  return {
    requiresRelationship: role.requiresRelationship,
    requiresAddress: role.requiresAddress,
    requiresPhone: role.requiresPhone,
  };
}

export default {
  ROLE_DEFINITIONS,
  DOCUMENT_TYPE_ROLES,
  ROLE_CATEGORIES,
  ROLE_TYPES,
  RELATIONSHIP_OPTIONS,
  getRolesForDocumentType,
  getRolesForDocumentTypes,
  getRoleTypesForDocumentType,
  getRoleTypesForDocumentTypes,
  getRoleDefinition,
  generatePlaceholdersForRole,
  generatePlaceholdersForCoFiduciaries,
  getRoleRequirements,
};
